import { vec3, mat4, vec2, vec4 } from "gl-matrix";
import { wLine } from "./wLine";
import { wCircle } from "./wCircle";
import { wTriangle } from "./wTriangle";
import { wMath } from "./wMath";
import { MathConst } from "./wContants";
import { wRay } from "./wRay";
import { wHitRecord } from "./wHitRecord";

const wGizmoMode = {
    TRANSLATE: 0,
    ROTATE: 1,
    SCALE: 2,
    TRACKBALL: 3,
    INACTIVE: 4,
};

const wGizmoAxis = {
    NO_AXIS: -1,
    X_AXIS: 0,
    Y_AXIS: 1,
    Z_AXIS: 2,
};

class wGizmo {
    constructor(size) {
        this.gizmoSize_ = size !== undefined ? size : 5.0;
        this.intersectionTolerance_ = 0.1;
        this.grappingPivotPoint_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.pivot_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.gizmoMode_ = wGizmoMode.TRANSLATE;

        //geometry
        this.axisLines_ = [];
        this.axisCircles_ = [];

        this.selectedAxis_ = wGizmoAxis.X_AXIS;
        this.hoverAxis_ = wGizmoAxis.X_AXIS;
        this.isDragging_ = false;
        this.isHovering_ = false;
        this.isActive_ = false;

        this.transform_ = mat4.create();

        //translation
        this.translation_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.startTranslation_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.translationDelta_ = vec3.fromValues(0.0, 0.0, 0.0);

        //rotation
        this.rotationPivot_ = mat4.create();
        this.rotation_ = mat4.create();

        this.deltaAngle_ = 0.0;
        this.circlePoint_ = vec3.fromValues(0.0, 0.0, 0.0);

        this.snapToAngle_ = false;
        this.snapAngle_ = (Math.PI * 2.0) / 20.0;

        this.rotationUpdateStartPoint_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.rotationUpdatePoint_ = vec3.fromValues(0.0, 0.0, 0.0);

        //scale
        this.scalePivot_ = vec3.fromValues(1.0, 1.0, 1.0);
        this.scale_ = vec3.fromValues(1.0, 1.0, 1.0);
        this.mousePivotNdc_ = vec2.fromValues(0.0, 0.0);
        this.uniformScaleFactor_ = 0.25;
        this.uniformScaling_ = false;
        this.minScaling_ = 0.05;
        this.maxScaling_ = 100.0;

        this.cameraRay_ = new wRay();
        this.createGizmoGeometry();
    }

    setGizmoSize(size) {
        this.gizmoSize_ = size;
        this.createGizmoGeometry();
    }

    resetGizmo() {
        this.transform_ = mat4.create();
        this.translation_ = vec3.fromValues(0.0, 0.0, 0.0);
        this.rotation_ = mat4.create();
        this.rotationPivot_ = mat4.create();
        this.scale_ = vec3.fromValues(1.0, 1.0, 1.0);
        this.scalePivot_ = this.scale_;
    }

    setGizmoMode(mode) {
        this.gizmoMode_ = mode;
    }

    getGizmoMode() {
        return this.gizmoMode_;
    }

    createGizmoGeometry() {
        let v0 = vec3.fromValues(0.0, 0.0, 0.0);
        let vX = vec3.fromValues(1.0, 0.0, 0.0);
        let vY = vec3.fromValues(0.0, 1.0, 0.0);
        let vZ = vec3.fromValues(0.0, 0.0, 1.0);
        let vXs = vec3.fromValues(this.gizmoSize_, 0.0, 0.0);
        let vYs = vec3.fromValues(0.0, this.gizmoSize_, 0.0);
        let vZs = vec3.fromValues(0.0, 0.0, this.gizmoSize_);
        let vGs = vec3.fromValues(
            this.gizmoSize_ * 0.5,
            this.gizmoSize_ * 0.5,
            this.gizmoSize_ * 0.5
        );

        // create the lines
        this.axisLines_[0] = new wLine(v0, vXs);
        this.axisLines_[1] = new wLine(v0, vYs);
        this.axisLines_[2] = new wLine(v0, vZs);

        this.axisCircles_[0] = new wCircle(vGs, vX, this.gizmoSize_ * 0.5, 24);
        this.axisCircles_[1] = new wCircle(vGs, vY, this.gizmoSize_ * 0.5, 24);
        this.axisCircles_[2] = new wCircle(vGs, vZ, this.gizmoSize_ * 0.5, 24);

        let triVertices = [];

        for (let i = 0; i < 3; i++) {
            let axisLine = this.getAxisLine(i);
            triVertices[i] = axisLine.getPoint(0.5);
        }

        this.scaleTriangle_ = new wTriangle(
            triVertices[0],
            triVertices[1],
            triVertices[2]
        );
    }

    getAxisLine(idx) {
        let l = new wLine();
        l.setLine(this.axisLines_[idx].p0_, this.axisLines_[idx].p1_);
        let t = vec3.create();
        vec3.add(t, this.translation_, this.pivot_);
        l.translate(t);
        return l;
    }

    getAxisCircle(idx) {
        const axisCircle = this.axisCircles_[idx];
        let c = new wCircle(axisCircle.getCenterPoint(), axisCircle.getNormal(), axisCircle.getRadius() * 2.0, 32);
        let t = vec3.create();
        vec3.add(t, this.translation_, this.pivot_);
        vec3.sub(t, t, vec3.fromValues(0.5 * this.gizmoSize_, 0.5 * this.gizmoSize_, 0.5 * this.gizmoSize_));
        c.translate(t);
        return c;
    }

    getScaleTriangle() {
        const tri = this.scaleTriangle_;
        let scaleTri = new wTriangle(tri.p0_,tri.p1_,tri.p2_);
        let t = vec3.create();
        vec3.add(t, this.translation_, this.pivot_);
        scaleTri.translate(t);
        return scaleTri;
    }

    update(mouseRay, mousePos, isLeftMouseDown) {
        if (this.gizmoMode_ == wGizmoMode.INACTIVE) {
            return false;
        }
        this.cameraRay_ = mouseRay;
        this.isHovering_ = false;
        let transformUpdated = false;

        switch (this.gizmoMode_) {
            case wGizmoMode.TRANSLATE:
                if (isLeftMouseDown) {
                    this.updateTranslation();
                    transformUpdated = true;
                } else {
                    this.updateHover();
                    this.isDragging_ = false;
                }
                break;
            case wGizmoMode.ROTATE:
                if (isLeftMouseDown) {
                    this.updateRotation();
                    transformUpdated = true;
                } else {
                    this.updateHover();
                    this.isDragging_ = false;
                }
                break;
            case wGizmoMode.SCALE:
                if (isLeftMouseDown) {
                    this.updateScale(mousePos);
                    transformUpdated = true;
                } else {
                    this.updateHover();
                    this.isDragging_ = false;
                }
                break;
        }

        this.updateTransform();
        return transformUpdated;
    }

    resetHover() {
        this.isHovering_ = false;
    }
    resetTranslation() {
        this.transform_ = mat4.create();
        this.translation_ = vec3.fromValues(0.0, 0.0, 0.0);
    }

    setPivot(pivot) {
        this.pivot_ = pivot;
        this.resetGizmo();
    }

    updateTranslation() {
        let lineIntersections = vec3.create();
        let rayT = vec3.create();
        let r = 10000.0;
        let l = 10000.0;

        for (let i = 0; i < 3; i++) {
            [l, r] = this.getAxisLine(i).rayLineClosestDistance(this.cameraRay_);
            lineIntersections[i] = l;
            rayT[i] = r;
        }

        for (let i = 0; i < 3; i++) {
            let closestPointOnLine = this.getAxisLine(i).getPoint(lineIntersections[i]);
            let distToCamera = vec3.create();
            let distToCameraEps = vec3.create();
            vec3.subtract(
                distToCamera,
                this.cameraRay_.getOrigin(),
                closestPointOnLine
            );
            vec3.subtract(
                distToCameraEps,
                this.cameraRay_.evaluateRay(0.01),
                closestPointOnLine
            );

            // invalidate lines where the ray is pointing in the opposite direction by check whether the origin is closer than epsilon along the ray
            if (vec3.length(distToCamera) < vec3.length(distToCameraEps)) {
                lineIntersections[i] = 1000.0;
            }
        }

        const isecAxis = wMath.getIndexOfMinComponent(lineIntersections);
        const isIntersectingLine = lineIntersections[isecAxis] < this.intersectionTolerance_;

        if (!this.isDragging_ && isIntersectingLine) {
            this.isDragging_ = true;
            this.selectedAxis_ = isecAxis;
            vec3.copy(this.grappingPivotPoint_, this.cameraRay_.evaluateRay(rayT[isecAxis]));
            vec3.copy(this.startTranslation_, this.translation_);

        } else if (this.isDragging_) {
            let isecPoint = this.cameraRay_.evaluateRay(rayT[this.selectedAxis_]);
            vec3.subtract(this.translationDelta_, isecPoint, this.grappingPivotPoint_);

            for (let i = 0; i < 3; i++) {
                if (i != this.selectedAxis_) {
                    this.translationDelta_[i] = 0.0; // only translate along the selected axis so the other are set to zero
                }
                vec3.add(this.translation_, this.startTranslation_, this.translationDelta_);
            }
        }
    }

    updateRotation() {
        let circleIntersections = vec3.create();
        let rayT = vec3.create();
        let r = 10000.0;
        let l = 10000.0;

        for (let i = 0; i < 3; i++) {
            let c = this.getAxisCircle(i);
            [l, r] = c.rayCircleClosestDistance(this.cameraRay_);
            circleIntersections[i] = l;
            rayT[i] = r;
        }

        const isecAxis = wMath.getIndexOfMinComponent(circleIntersections);
        const isIntersectingLine = circleIntersections[isecAxis] < this.intersectionTolerance_;

        if (!this.isDragging_ && isIntersectingLine) {
            this.isDragging_ = true;
            this.selectedAxis_ = isecAxis;
            this.grappingPivotPoint_ = this.cameraRay_.evaluateRay(rayT[isecAxis]);
            let circleCenter = this.getAxisCircle(isecAxis).getCenterPoint();
            let tmp = vec3.create();
            let tmp2 = vec3.create();
            let tmp3 = vec3.create();
            vec3.subtract(tmp, this.grappingPivotPoint_, circleCenter);
            vec3.normalize(tmp2, tmp);
            vec3.scale(tmp3, tmp2, this.gizmoSize_);
            vec3.add(this.rotationUpdateStartPoint_, circleCenter, tmp3);
            vec3.copy(this.rotationUpdatePoint_, this.rotationUpdateStartPoint_);
            mat4.copy(this.rotationPivot_, this.rotation_);

        } else if (this.isDragging_) { // update dragging
            let isecPoint = this.cameraRay_.evaluateRay(rayT[this.selectedAxis_]);
            this.circlePoint_ = isecPoint;
            let c = this.getAxisCircle(this.selectedAxis_);
            let v0 = vec3.create();
            let v1 = vec3.create();
            vec3.subtract(v0, this.grappingPivotPoint_, c.getCenterPoint());
            vec3.subtract(v1, this.circlePoint_, c.getCenterPoint());

            let v0v1 = vec3.create();
            vec3.subtract(v0v1, v1, v0);

            if (vec3.length(v0v1) < MathConst.fEps) {
                return;
            }

            let x = vec3.create();
            vec3.cross(x, v0, v1);
            let sign = vec3.dot(x, this.axisCircles_[this.selectedAxis_].getNormal()) < 0.0 ? -1.0 : 1.0;
            let nV0 = vec3.create();
            let nV1 = vec3.create();

            vec3.normalize(nV0, v0);
            vec3.normalize(nV1, v1);
            this.deltaAngle_ = Math.acos(vec3.dot(nV0, nV1)) * sign;
            const rotationAxis = this.getRotationAxis(this.selectedAxis_);
            mat4.rotate(
                this.rotation_,
                this.rotationPivot_,
                this.deltaAngle_,
                rotationAxis
            );

            let tmp = vec3.create();
            vec3.scale(tmp, nV1, this.gizmoSize_);
            vec3.add(this.rotationUpdatePoint_, c.getCenterPoint(), tmp);
        }
    }

    getAxisRotationPoints() {
        
        let points = [];
        const c = this.getAxisCircle(this.selectedAxis_);
        let v0 = vec3.create();
        let v1 = vec3.create();
        let nV0 = vec3.create();
        let nV1 = vec3.create();

        vec3.subtract(v0, this.rotationUpdateStartPoint_, c.getCenterPoint());
        vec3.subtract(v1, this.rotationUpdatePoint_, c.getCenterPoint());

        vec3.normalize(nV0, v0);
        vec3.normalize(nV1, v1);

        let x = vec3.create();
        vec3.cross(x, v0, v1);
        let sign = vec3.dot(x, this.axisCircles_[this.selectedAxis_].getNormal()) < 0.0 ? -1.0 : 1.0;
        let angle = Math.acos(vec3.dot(nV0, nV1));// * sign;

        let numSamples = parseInt( angle / (3.1415 * 0.05));

        console.log("angle:" + angle);
        console.log("num samples:" + numSamples);
        
    }

    updateScale(mouseNdc) {
        let hitRecord = this.getScaleTriangle().rayIntersect(this.cameraRay_);
        let lineIntersections = vec3.create();
        let rayT = vec3.create();
        let l = 10000.0;
        let r = 10000.0;

        for (let i = 0; i < 3; i++) {
            [l, r] = this.getAxisLine(i).rayLineClosestDistance(this.cameraRay_);
            lineIntersections[i] = l;
            rayT[i] = r;
        }

        let isecAxis = wMath.getIndexOfMinComponent(lineIntersections);
        let isIntersectingLine = lineIntersections[isecAxis] < this.intersectionTolerance_;

        if (!this.isDragging_ && hitRecord.isValidHit()) {
            this.isDragging_ = true;
            vec3.copy(this.grappingPivotPoint_,this.cameraRay_.evaluateRay(hitRecord.hitTimes_[0]));
            vec3.copy(this.scalePivot_, this.scale_);
            this.mousePivotNdc_ = mouseNdc;
            this.uniformScaling_ = true;
            this.selectedAxis_ = wGizmoAxis.NO_AXIS;

        } else if (!this.isDragging_ && isIntersectingLine) {
            this.isDragging_ = true;
            this.selectedAxis_ = isecAxis;
            this.grappingPivotPoint_ = this.cameraRay_.evaluateRay(rayT[isecAxis]);
            vec3.copy(this.scalePivot_, this.scale_);
            this.uniformScaling_ = false;
        } else if (this.isDragging_) {
            if (this.uniformScaling_) {
                let mm = vec2.create();
                vec2.subtract(mm, mouseNdc, this.mousePivotNdc_);
                let mProj = wMath.vectorProjectionV2(mm,vec2.fromValues(0.0, 1.0));
                let projLen = vec2.length(mProj);
                let uniformScaleDelta = (mProj[1] > 0.0) ? projLen * this.uniformScaleFactor_ : -projLen * this.uniformScaleFactor_;
                let sc = vec3.create();
                vec3.add(sc, this.scalePivot_, vec3.fromValues(uniformScaleDelta,uniformScaleDelta, uniformScaleDelta));
                vec3.copy( this.scale_, wMath.clamp(sc, this.minScaling_, this.maxScaling_) );

            } else {
                let isecPoint = this.cameraRay_.evaluateRay(rayT[this.selectedAxis_]);
                let deltaVec = vec3.create();
                vec3.subtract(deltaVec, isecPoint, this.grappingPivotPoint_);
                vec3.scale(deltaVec,deltaVec,this.uniformScaleFactor_ * 0.2);
               
                for (let i = 0; i < 3; i++) {
                    if (i == this.selectedAxis_) continue;
                    deltaVec[i] = 0.0;
                }

                let scaleDelta = vec3.length(deltaVec);
                let sgn = vec3.dot(deltaVec,this.getAxisLine(this.selectedAxis_).getLineDirection());
                let scaleDeltaVec = vec3.fromValues(0.0, 0.0, 0.0);
                scaleDeltaVec[this.selectedAxis_] = (sgn > 0.0) ? scaleDelta : -scaleDelta;
                let sc = vec3.create();
                vec3.add(sc, this.scalePivot_, scaleDeltaVec);
                vec3.copy(this.scale_,wMath.clamp(sc, this.minScaling_, this.maxScaling_));
            }
        }
    }

    updateTransform() {
        let t1 = mat4.create();
        let t2 = mat4.create();
        let rot = mat4.create();
        let rot1 = mat4.create();
        let negP = vec3.create();

        mat4.fromTranslation(t1, this.pivot_);
        vec3.scale(negP, this.pivot_, -1.0);
        mat4.fromTranslation(t2, negP);
        mat4.multiply(rot1, this.rotation_, t2);
        mat4.multiply(rot, t1, rot1);

        mat4.fromTranslation(t1, this.translation_);
        mat4.fromScaling(t2, this.scale_);
       /* mat4.multiply(rot1, rot, t2);
        mat4.multiply(rot, t1, rot1);
        mat4.copy(this.transform_, rot);*/

       // mat4.multiply(rot1, rot, t2);
        mat4.multiply(rot1, t2, rot);
        mat4.multiply(rot, t1, rot1);

        mat4.copy(this.transform_, rot);
    }

    getOrientation() {
        let t1 = mat4.create();
        let t2 = mat4.create();
        let rot = mat4.create();
        let rot1 = mat4.create();
        let negP = vec3.create();

        mat4.fromTranslation(t1, this.pivot_);
        vec3.scale(negP, this.pivot_, -1.0);
        mat4.fromTranslation(t2, negP);
        mat4.multiply(rot1, this.rotation_, t2);
        mat4.multiply(rot, t1, rot1);
        return rot;
    }

    updateHover() {
        let intersections = vec3.fromValues(10000.0, 10000.0, 10000.0);
        let rayT = vec3.create();
        let isecAxis = 1000.0;
        let isIntersecting = false;
        let l = 10000.0;
        let r = 10000.0;
        this.isHovering_ = false;

        switch (this.gizmoMode_) {
            case wGizmoMode.TRANSLATE:
                for (let i = 0; i < 3; i++) {
                    [l, r] = this.getAxisLine(i).rayLineClosestDistance(
                        this.cameraRay_
                    );
                    intersections[i] = l;
                    rayT[i] = r;
                }
                isecAxis = wMath.getIndexOfMinComponent(intersections);
                isIntersecting =
                    intersections[isecAxis] < this.intersectionTolerance_;
                if (isIntersecting) {
                    this.hoverAxis_ = isecAxis;
                    this.isHovering_ = true;
                }

                break;
            case wGizmoMode.ROTATE:
                for (let i = 0; i < 3; i++) {
                    let c = this.getAxisCircle(i);
                    [l, r] = c.rayCircleClosestDistance(this.cameraRay_);
                    intersections[i] = l;
                    rayT[i] = r;
                }

                isecAxis = wMath.getIndexOfMinComponent(intersections);
                isIntersecting = intersections[isecAxis] < this.intersectionTolerance_;
                if (isIntersecting) {
                    this.hoverAxis_ = isecAxis;
                    this.isHovering_ = true;
                }
                break;
            case wGizmoMode.SCALE:
                {
                    let hitRecord = this.getScaleTriangle().rayIntersect(this.cameraRay_);
                  
                    if (hitRecord.isValidHit()) {
                        this.isHovering_ = true;
                        this.hoverAxis_ = wGizmoAxis.NO_AXIS;
                    }
                    else {
                        for (let i = 0; i < 3; i++) {
                            [l, r] = this.getAxisLine(i).rayLineClosestDistance(
                                this.cameraRay_
                            );
                            intersections[i] = l;
                            rayT[i] = r;
                        }
                        isecAxis = wMath.getIndexOfMinComponent(intersections);
                        isIntersecting = intersections[isecAxis] < this.intersectionTolerance_;
                        if (isIntersecting) {
                            this.isHovering_ = true;
                            this.hoverAxis_ = isecAxis;   
                        }
                    }
                }
                break;
        }
    }

    getRotationAxis(selected) {
        let invR = mat4.create();
        mat4.invert(invR, this.rotationPivot_);
        const rAxis = vec3.fromValues( invR[selected * 4 + 0],invR[selected * 4 + 1], invR[selected * 4 + 2]);
        return rAxis;
    }

    isDragging() {
        return this.isDragging_;
    }

    isHovering() {
        return this.isHovering_;
    }

    isUniformScaling(){
        return this.uniformScaling_;
    }

    getSelectedAxis() {
        return this.selectedAxis_;
    }

    getHoverAxis() {
        return this.hoverAxis_;
    }
}
export { wGizmo, wGizmoMode, wGizmoAxis };
